/**
 * Browser diagnostics for the version overlay's copy action (HS-1222).
 *
 * The report exists to be pasted into a bug ticket, so its labels are **deliberately not
 * translated**: a French-localized report is harder for the team reading the ticket, and these
 * are technical field names in the sense that HTTP header names are. Only the button's
 * accessible name and its confirmation come from `UiMessages`.
 *
 * WHAT THIS MUST NEVER READ: cookies, storage contents, auth tokens, or anything identifying the
 * signed-in user. The report is what any site can already observe about the browser, and it goes
 * into a Jira comment. `collectDiagnostics.test.ts` asserts this.
 */

/** Half of these are non-standard and absent in Firefox and Safari — every read is guarded. */
interface NavigatorExtras {
  userAgentData?: {
    brands?: { brand: string; version: string }[];
    platform?: string;
  };
  deviceMemory?: number;
  connection?: {
    effectiveType?: string;
    downlink?: number;
    rtt?: number;
  };
}

/** One `Label: value` line. A collector returning `null` contributes nothing to the report. */
type Line = readonly [label: string, value: string | null];

const NOT_A_BRAND = /not.a.brand/i;

/** `navigator.userAgentData.brands` carries a deliberate decoy entry — drop it. */
function browserFromUserAgentData(nav: Navigator & NavigatorExtras): string | null {
  const brands = nav.userAgentData?.brands;
  if (!brands?.length) return null;

  const real = brands.filter((entry) => !NOT_A_BRAND.test(entry.brand));
  const shown = real.length > 0 ? real : brands;

  return shown.map((entry) => `${entry.brand} ${entry.version}`).join(', ');
}

function colourScheme(win: Window): string | null {
  if (typeof win.matchMedia !== 'function') return null;

  const prefersDark = win.matchMedia('(prefers-color-scheme: dark)').matches;
  const pinned = win.document.documentElement.dataset.theme;

  // Both apps stamp an explicit `data-theme` on <html> for a Light or Dark choice, and Light is
  // the default (HS-1533), so the effective theme and the OS preference routinely disagree. A
  // report showing only one of the two would send a reader hunting for a theme bug that isn't there.
  return pinned
    ? `${pinned} (pinned) · prefers ${prefersDark ? 'dark' : 'light'}`
    : prefersDark
      ? 'dark'
      : 'light';
}

function reducedMotion(win: Window): string | null {
  if (typeof win.matchMedia !== 'function') return null;
  return win.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'reduce' : 'no-preference';
}

function connection(nav: Navigator & NavigatorExtras): string | null {
  const conn = nav.connection;
  if (!conn) return null;

  const parts = [
    conn.effectiveType,
    conn.downlink === undefined ? null : `${String(conn.downlink)} Mbps`,
    conn.rtt === undefined ? null : `RTT ${String(conn.rtt)} ms`,
  ].filter((part): part is string => Boolean(part));

  return parts.length > 0 ? parts.join(' · ') : null;
}

function viewport(win: Window): string {
  const dpr = win.devicePixelRatio;
  const size = `${String(win.innerWidth)}×${String(win.innerHeight)}`;
  return dpr && dpr !== 1 ? `${size} · DPR ${String(dpr)}` : size;
}

function screenSize(win: Window): string {
  const { screen } = win;
  return `${String(screen.width)}×${String(screen.height)}`;
}

function timeZone(): string | null {
  const resolved = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return resolved || null;
}

/**
 * Build the diagnostics half of the copied report — everything after the version entries.
 *
 * `now` is injected rather than read here so a test can assert an exact line, and so the caller
 * stamps the moment the user actually clicked.
 */
export function collectDiagnostics(win: Window, now: Date): string[] {
  const nav = win.navigator as Navigator & NavigatorExtras;

  const lines: Line[] = [
    ['URL', win.location.href],
    ['Timestamp', now.toISOString()],
    ['Browser', browserFromUserAgentData(nav)],
    ['Platform', nav.userAgentData?.platform ?? null],
    ['User agent', nav.userAgent],
    ['Languages', nav.languages.length > 0 ? nav.languages.join(', ') : nav.language],
    ['Time zone', timeZone()],
    ['Viewport', viewport(win)],
    ['Screen', screenSize(win)],
    ['Colour scheme', colourScheme(win)],
    ['Reduced motion', reducedMotion(win)],
    ['Online', nav.onLine ? 'yes' : 'no'],
    ['Cookies enabled', nav.cookieEnabled ? 'yes' : 'no'],
    ['CPU cores', nav.hardwareConcurrency ? String(nav.hardwareConcurrency) : null],
    ['Device memory', nav.deviceMemory ? `${String(nav.deviceMemory)} GB` : null],
    ['Connection', connection(nav)],
  ];

  return lines
    .filter((line): line is readonly [string, string] => Boolean(line[1]))
    .map(([label, value]) => `${label}: ${value}`);
}
